import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    type PanResponderGestureState,
    PanResponder,
    AppState,
    Animated,
    Platform
} from "react-native";
import type {
    ToastStackMotionConstantType,
    ToastSwipeConstantType,
    ToastStackMotionType,
    ToastSwipeAxis,
    ToastSwipeType
} from "./type";
import {
    getIsToastSwipeDismissed,
    getToastSwipeAmount,
    getToastSwipeSign,
    TOAST_SWIPE
} from "./util";
import {
    useAnimatedState,
    MOTION_SPRINGS
} from "../../helpers/motion";

export const useToastBackground = (): boolean => {
    const [
        isBackground,
        setIsBackground
    ] = useState(AppState.currentState === "background");

    useEffect(() => {
        const subscription = AppState.addEventListener("change", (state) => {
            setIsBackground(state === "background");
        });

        return () => {
            subscription?.remove();
        };
    }, []);

    return isBackground;
};

export const useToastStackMotion = ({
    customTheme,
    target
}: ToastStackMotionConstantType): ToastStackMotionType => {
    const translateX = useAnimatedState({
        value: target.translateX,
        intent: "stack",
        customTheme
    });

    const translateY = useAnimatedState({
        value: target.translateY,
        intent: "stack",
        customTheme
    });

    const opacity = useAnimatedState({
        value: target.opacity,
        intent: "stack",
        customTheme
    });

    const scaleX = useAnimatedState({
        value: target.scaleX,
        intent: "stack",
        customTheme
    });

    const scaleY = useAnimatedState({
        value: target.scaleY,
        intent: "stack",
        customTheme
    });

    const cover = useAnimatedState({
        value: target.cover,
        intent: "stack",
        customTheme
    });

    return {
        translateX,
        translateY,
        opacity,
        scaleX,
        scaleY,
        cover
    };
};

export const useToastSwipe = ({
    isReduceMotionEnabled,
    direction,
    isEnabled,
    onDismiss,
    align,
    side
}: ToastSwipeConstantType): ToastSwipeType => {
    const swipeX = useRef(new Animated.Value(0)).current;

    const swipeY = useRef(new Animated.Value(0)).current;

    const axisRef = useRef<ToastSwipeAxis | undefined>(undefined);

    const startRef = useRef(0);

    const startTimeRef = useRef(0);

    const amountRef = useRef(0);

    const valueXRef = useRef(0);

    const valueYRef = useRef(0);

    const isNativeDriver = Platform.OS !== "web";

    const [
        isSwiping,
        setIsSwiping
    ] = useState(false);

    const getSign = (axis: ToastSwipeAxis) => getToastSwipeSign({
        direction,
        align,
        axis,
        side
    });

    const getIsStarted = ({
        dx,
        dy
    }: PanResponderGestureState): boolean => {
        const distanceX = Math.abs(dx);

        const distanceY = Math.abs(dy);

        if(!isEnabled || Math.max(distanceX, distanceY) < TOAST_SWIPE.slop) {
            return false;
        }

        const axis: ToastSwipeAxis = distanceY >= distanceX ? "y" : "x";

        if(!getSign(axis)) {
            return false;
        }

        axisRef.current = axis;

        return true;
    };

    const handleRest = () => {
        if(isReduceMotionEnabled) {
            swipeX.setValue(0);

            swipeY.setValue(0);

            return;
        }

        Animated.parallel([
            Animated.spring(swipeX, {
                ...MOTION_SPRINGS.standard,
                useNativeDriver: isNativeDriver,
                toValue: 0
            }),
            Animated.spring(swipeY, {
                ...MOTION_SPRINGS.standard,
                useNativeDriver: isNativeDriver,
                toValue: 0
            })
        ]).start();
    };

    const handleGrant = () => {
        swipeX.stopAnimation();

        swipeY.stopAnimation();

        startRef.current = axisRef.current === "x" ? valueXRef.current : valueYRef.current;

        startTimeRef.current = Date.now();

        amountRef.current = startRef.current;

        setIsSwiping(true);
    };

    const handleMove = ({
        dx,
        dy
    }: PanResponderGestureState) => {
        const axis = axisRef.current;

        if(!axis) {
            return;
        }

        const amount = getToastSwipeAmount({
            delta: startRef.current + (axis === "y" ? dy : dx),
            sign: getSign(axis)
        });

        amountRef.current = amount;

        (axis === "y" ? swipeY : swipeX).setValue(amount);
    };

    const handleRelease = () => {
        setIsSwiping(false);

        const axis = axisRef.current;

        if(!axis) {
            return;
        }

        const sign = getSign(axis);

        const amount = amountRef.current;

        if(getIsToastSwipeDismissed({
            elapsed: Date.now() - startTimeRef.current,
            amount,
            sign
        })) {
            onDismiss({
                amount,
                axis,
                sign
            });

            return;
        }

        handleRest();
    };

    const handleCancel = () => {
        setIsSwiping(false);

        handleRest();
    };

    const swipeActions = useRef({
        handleRelease,
        getIsStarted,
        handleCancel,
        handleGrant,
        handleMove
    });

    const panResponder = useRef(PanResponder.create({
        onMoveShouldSetPanResponderCapture: (_, gestureState) => swipeActions.current.getIsStarted(gestureState),
        onPanResponderMove: (_, gestureState) => swipeActions.current.handleMove(gestureState),
        onPanResponderTerminate: () => swipeActions.current.handleCancel(),
        onPanResponderRelease: () => swipeActions.current.handleRelease(),
        onPanResponderGrant: () => swipeActions.current.handleGrant(),
        onPanResponderTerminationRequest: () => false,
        onStartShouldSetPanResponder: () => false,
        onShouldBlockNativeResponder: () => true
    })).current;

    useEffect(() => {
        swipeActions.current = {
            handleRelease,
            getIsStarted,
            handleCancel,
            handleGrant,
            handleMove
        };
    });

    useEffect(() => {
        const listenerX = swipeX.addListener(({
            value
        }) => {
            valueXRef.current = value;
        });

        const listenerY = swipeY.addListener(({
            value
        }) => {
            valueYRef.current = value;
        });

        return () => {
            swipeX.removeListener(listenerX);

            swipeY.removeListener(listenerY);

            swipeX.stopAnimation();

            swipeY.stopAnimation();
        };
    }, []);

    return {
        panHandlers: panResponder.panHandlers,
        isSwiping,
        swipeX,
        swipeY
    };
};
